import { Loader, Stacked } from '@statsbomb/kitbag-components';
import { PlayerAvatar, TeamAvatar } from '@statsbomb/kitbag-global-football';
import { Atom, useAtomValue } from 'jotai';
import { ReactNode, Suspense } from 'react';
import { secureBucketCookieRequestedAtom } from '@/atoms/cookies';
import { playerIdAtom } from '@/atoms/player/player';
import { selectedTeamInfoAtom } from '@/atoms/queries/teams';
import { teamIdAtom } from '@/atoms/team/team';
import { useGetEntity } from '@/hooks/useGetEntity';
import { GenderIcon } from '../icons/GenderIcon';
import { TopBar } from './TopBar';
import { TopBarHeading } from './TopBarHeading';

const TeamTopBarHeader = ({ teamName }: { teamName?: string }) => {
  const selectedTeamInfo = useAtomValue(selectedTeamInfoAtom);
  const { gender = '' } = selectedTeamInfo ?? {};
  const teamId = useAtomValue(teamIdAtom);
  const secureBucketCookieRequested = useAtomValue(secureBucketCookieRequestedAtom);

  return (
    <Stacked>
      <TeamAvatar teamId={secureBucketCookieRequested ? teamId : undefined} />
      <span className="flex items-center gap-1">
        {teamName}
        <GenderIcon gender={gender} />
      </span>
    </Stacked>
  );
};

interface EntityTopBarHeaderProps {
  selectedEntityNameAtom: Atom<Promise<string | undefined> | string>;
  actions?: ReactNode;
}

const EntityTopBarHeader = ({ selectedEntityNameAtom, actions }: EntityTopBarHeaderProps) => {
  const { entity } = useGetEntity();
  const entityName = useAtomValue(selectedEntityNameAtom);
  const playerId = useAtomValue(playerIdAtom);
  const secureBucketCookieRequested = useAtomValue(secureBucketCookieRequestedAtom);

  let header: ReactNode = entityName;
  if (entity === 'team') header = <TeamTopBarHeader teamName={entityName} />;
  else if (entity === 'player') {
    header = (
      <Stacked>
        <PlayerAvatar playerId={secureBucketCookieRequested ? playerId : undefined} playerName={entityName} />
        <span data-testid="top-bar-entity-name">{entityName}</span>
      </Stacked>
    );
  }

  return (
    <>
      <TopBarHeading>{header}</TopBarHeading>
      {actions}
    </>
  );
};

const EntityTopBarLoader = () => <Loader testId="loading-top-bar-entity" />;

export const EntityTopBar = ({
  isLoading = false,
  ...headerProps
}: EntityTopBarHeaderProps & { isLoading?: boolean }) => (
  <TopBar>
    {isLoading ? (
      <EntityTopBarLoader />
    ) : (
      <Suspense fallback={<EntityTopBarLoader />}>
        <EntityTopBarHeader {...headerProps} />
      </Suspense>
    )}
  </TopBar>
);
